/**
 * Copy-to-clipboard button with a transient "copied" confirmation. On clipboard
 * failure (blocked permission / insecure context) it invokes the optional
 * `onError` so the caller can degrade gracefully (e.g. open the link instead).
 *
 * Extracted so the QR-handoff screen can offer more than one copyable link (the
 * short-lived handoff URL and the long-lived collection URL) without duplicating
 * the write/confirm/reset dance.
 */
import { useState } from 'react';

interface CopyButtonProps {
  /** The exact string written to the clipboard. */
  value: string;
  /** Button label in the idle state. */
  idleLabel: string;
  /** Button label shown briefly after a successful copy. */
  copiedLabel: string;
  className?: string;
  /** Invoked when the clipboard write throws (permission blocked / insecure). */
  onError?: () => void;
}

export function CopyButton({ value, idleLabel, copiedLabel, className, onError }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      onError?.();
    }
  };

  return (
    <button type="button" className={className} onClick={() => void copy()}>
      {copied ? copiedLabel : idleLabel}
    </button>
  );
}
